import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { publicApi } from '../../services/publicApi';
import Breadcrumbs from '../../components/public/Breadcrumbs';

const DynamicPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  // Get slug from URL path
  const currentSlug = slug || location.pathname.replace('/', '') || 'home';

  useEffect(() => {
    loadPage();
  }, [currentSlug]);

  const loadPage = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await publicApi.getPage(currentSlug);
      
      if (response.data.page) {
        setPage(response.data.page);
        
        // Set up breadcrumbs
        const breadcrumbItems = [
          { title: 'Home', url: '/' },
          { title: response.data.page.title, current: true }
        ];
        
        // Add parent breadcrumbs if available
        if (response.data.breadcrumbs) {
          const parentBreadcrumbs = response.data.breadcrumbs.slice(0, -1);
          breadcrumbItems.splice(1, 0, ...parentBreadcrumbs);
        }
        
        setBreadcrumbs(breadcrumbItems);
        
        // Update page title
        document.title = `${response.data.page.title} - ${tenant?.name || 'Baseball Federation'}`;
        
        // Update meta description
        if (response.data.page.meta_description) {
          let metaDescription = document.querySelector('meta[name="description"]');
          if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.name = 'description';
            document.head.appendChild(metaDescription);
          }
          metaDescription.content = response.data.page.meta_description;
        }
        
      } else {
        setError('Page not found');
      }
    } catch (error) {
      console.error('Failed to load page:', error);
      if (error.response?.status === 404) {
        setError('Page not found');
      } else {
        setError('Failed to load page content');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPageContent = () => {
    if (!page) return null;

    switch (page.template) {
      case 'homepage':
        return <HomepageTemplate page={page} />;
      case 'content':
        return <ContentTemplate page={page} />;
      case 'events':
        return <EventsTemplate page={page} />;
      case 'gallery':
        return <GalleryTemplate page={page} />;
      case 'contact':
        return <ContactTemplate page={page} />;
      default:
        return <DefaultTemplate page={page} />;
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-container">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <p>Loading page content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-error">
        <div className="container">
          <div className="error-content">
            <div className="error-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h1>Page Not Found</h1>
            <p>{error}</p>
            <div className="error-actions">
              <button 
                onClick={() => navigate('/')} 
                className="btn btn-primary"
              >
                Go to Homepage
              </button>
              <button 
                onClick={() => window.history.back()} 
                className="btn btn-outline-secondary"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`dynamic-page template-${page?.template || 'default'}`}>
      <Breadcrumbs items={breadcrumbs} />
      {renderPageContent()}
    </div>
  );
};

// Default template for most pages
const DefaultTemplate = ({ page }) => (
  <div className="page-content">
    <div className="container">
      <header className="page-header">
        <h1>{page.title}</h1>
        {page.excerpt && (
          <p className="page-excerpt">{page.excerpt}</p>
        )}
      </header>
      
      <div className="page-body">
        {page.content && (
          <div 
            className="content-html"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        )}
      </div>
      
      {page.updated_at && (
        <footer className="page-footer">
          <p className="last-updated">
            <i className="fas fa-clock"></i>
            Last updated: {new Date(page.updated_at).toLocaleDateString()}
          </p>
        </footer>
      )}
    </div>
  </div>
);

// Content template with sidebar
const ContentTemplate = ({ page }) => (
  <div className="page-content content-template">
    <div className="container">
      <div className="content-layout">
        <main className="content-main">
          <header className="page-header">
            <h1>{page.title}</h1>
            {page.excerpt && (
              <p className="page-excerpt">{page.excerpt}</p>
            )}
          </header>
          
          <div className="page-body">
            {page.content && (
              <div 
                className="content-html"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            )}
          </div>
        </main>
        
        <aside className="content-sidebar">
          <div className="sidebar-section">
            <h3>Quick Links</h3>
            <ul className="sidebar-links">
              <li><a href="/rules">Rules & Regulations</a></li>
              <li><a href="/committees">Committees</a></li>
              <li><a href="/events">Events</a></li>
              <li><a href="/contact">Contact Us</a></li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  </div>
);

// Events template
const EventsTemplate = ({ page }) => (
  <div className="page-content events-template">
    <div className="container">
      <header className="page-header">
        <h1>{page.title}</h1>
        {page.excerpt && (
          <p className="page-excerpt">{page.excerpt}</p>
        )}
      </header>
      
      <div className="page-body">
        {page.content && (
          <div 
            className="content-html"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        )}
        
        {/* Event-specific content would be loaded here */}
        <div className="events-placeholder">
          <p>Event listings will be implemented in the next phase.</p>
        </div>
      </div>
    </div>
  </div>
);

// Gallery template
const GalleryTemplate = ({ page }) => (
  <div className="page-content gallery-template">
    <div className="container">
      <header className="page-header">
        <h1>{page.title}</h1>
        {page.excerpt && (
          <p className="page-excerpt">{page.excerpt}</p>
        )}
      </header>
      
      <div className="page-body">
        {page.content && (
          <div 
            className="content-html"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        )}
        
        {/* Gallery-specific content would be loaded here */}
        <div className="gallery-placeholder">
          <p>Gallery content will be implemented in the next phase.</p>
        </div>
      </div>
    </div>
  </div>
);

// Contact template
const ContactTemplate = ({ page }) => (
  <div className="page-content contact-template">
    <div className="container">
      <header className="page-header">
        <h1>{page.title}</h1>
        {page.excerpt && (
          <p className="page-excerpt">{page.excerpt}</p>
        )}
      </header>
      
      <div className="page-body">
        <div className="contact-layout">
          <div className="contact-info">
            {page.content && (
              <div 
                className="content-html"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            )}
          </div>
          
          <div className="contact-form">
            {/* Contact form would be implemented here */}
            <div className="form-placeholder">
              <p>Contact form will be implemented in the next phase.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Homepage template (for flexibility)
const HomepageTemplate = ({ page }) => (
  <div className="page-content homepage-template">
    <div className="container">
      <div className="homepage-content">
        {page.content && (
          <div 
            className="content-html"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        )}
      </div>
    </div>
  </div>
);

export default DynamicPage;